import { Aide } from '@/components/ui/aide';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { trucksAPI } from '../../lib/api';
import { inboundRentalsAPI } from '../../lib/inboundRentalsApi';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatMontant } from '../../lib/formats';
import { libelle, nomLisible } from '../../lib/libelles';
import { truckOption } from '../../lib/selectOptions';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { EmptyState } from '../ui/empty-state';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

/** Aujourd'hui en date de calendrier locale `AAAA-MM-JJ`. */
export const aujourdhuiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Où en est un camion du contrat aujourd'hui: pas encore commencé, dans la
 * flotte, ou rendu au propriétaire. Les dates viennent du serveur
 * (`effective_*`), jamais recalculées d'après les tarifs.
 */
export const situationCamion = (ligne, aujourdhui = aujourdhuiIso()) => {
  const debut = ligne.effective_start_date || '';
  const fin = ligne.effective_end_date || '';
  if (debut && debut > aujourdhui) return 'A_VENIR';
  if (fin && fin < aujourdhui) return 'RENDU';
  return ligne.in_fleet_today === false ? 'RENDU' : 'EN_COURS';
};

const SITUATIONS = {
  A_VENIR: { texte: 'Pas encore commencé', variant: 'info' },
  EN_COURS: { texte: 'Dans votre flotte', variant: 'success' },
  RENDU: { texte: libelle('truckStatus', 'RETURNED_TO_OWNER'), variant: 'muted' },
};

const formVide = () => ({ truck_id: '', start_date: '', end_date: '', rate_amount: '' });

const InboundTrucksPanel = ({ contract, canSeeMoney, onChanged }) => {
  const lignes = useMemo(() => contract.trucks || [], [contract.trucks]);
  const modifiable = contract.status !== 'CLOS';
  const [allTrucks, setAllTrucks] = useState([]);
  const [dialog, setDialog] = useState(null); // { ligne? }
  const [form, setForm] = useState(formVide);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aRetirer, setARetirer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAllPages((params) => trucksAPI.getAllPaged(params))
      .then(({ items }) => {
        if (!cancelled) setAllTrucks(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const dejaDansLeContrat = useMemo(() => new Set(lignes.map((ligne) => ligne.truck_id)), [lignes]);
  const options = useMemo(
    () => allTrucks.filter((truck) => !dejaDansLeContrat.has(truck.id)).map(truckOption),
    [allTrucks, dejaDansLeContrat],
  );

  const ouvrirAjout = () => {
    setForm(formVide());
    setSubmitAttempted(false);
    setDialog({});
  };
  const ouvrirModification = (ligne) => {
    setForm({
      truck_id: ligne.truck_id,
      start_date: ligne.start_date || '',
      end_date: ligne.end_date || '',
      rate_amount: ligne.rate_amount === null || ligne.rate_amount === undefined ? '' : String(ligne.rate_amount),
    });
    setSubmitAttempted(false);
    setDialog({ ligne });
  };
  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const enregistrer = async () => {
    const edition = Boolean(dialog?.ligne);
    setSubmitAttempted(true);
    if (!edition && !form.truck_id) {
      toast.error('Choisissez le camion à ajouter');
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error('La date de fin ne peut pas précéder la date de début');
      return;
    }
    setBusy(true);
    try {
      const tarif = form.rate_amount === '' ? null : Number(form.rate_amount);
      if (edition) {
        const corps = { start_date: form.start_date || null, end_date: form.end_date || null };
        if (canSeeMoney) corps.rate_amount = tarif;
        await inboundRentalsAPI.updateTruck(dialog.ligne.id, corps);
        toast.success('Camion mis à jour');
      } else {
        const corps = { truck_id: form.truck_id };
        if (form.start_date) corps.start_date = form.start_date;
        if (form.end_date) corps.end_date = form.end_date;
        if (canSeeMoney && tarif !== null) corps.rate_amount = tarif;
        await inboundRentalsAPI.addTruck(contract.id, corps);
        toast.success('Camion ajouté au contrat');
      }
      setDialog(null);
      await onChanged();
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement impossible'));
    } finally {
      setBusy(false);
    }
  };

  const retirer = async () => {
    if (!aRetirer) return;
    setBusy(true);
    try {
      await inboundRentalsAPI.removeTruck(aRetirer.id);
      toast.success('Camion retiré du contrat');
      setARetirer(null);
      await onChanged();
    } catch (error) {
      toast.error(
        messageErreur(error, 'Retrait impossible : si le camion a déjà été réglé, donnez-lui une date de fin.'),
      );
      setARetirer(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="inbound-trucks-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Aide titre="Dates des camions" libelle="Dates par camion">
          Chaque camion a ses propres dates{canSeeMoney ? ' et peut avoir son propre tarif' : ''}. Sans date,
          il suit celles du contrat.
        </Aide>
        {modifiable ? (
          <Button onClick={ouvrirAjout} data-testid="add-inbound-truck-btn">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Ajouter un camion
          </Button>
        ) : null}
      </div>

      {lignes.length === 0 ? (
        <EmptyState
          illustration="donnee"
          title="Aucun camion dans ce contrat"
          description="Ajoutez les camions que ce transporteur met à votre disposition. Un contrat sans camion ne peut pas être activé."
          actionLabel={modifiable ? '+ Ajouter un camion' : undefined}
          onAction={modifiable ? ouvrirAjout : undefined}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border/70">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracteur</TableHead>
                <TableHead>Citerne</TableHead>
                <TableHead>Début</TableHead>
                <TableHead>Fin</TableHead>
                {canSeeMoney ? <TableHead className="text-right">Tarif propre</TableHead> : null}
                <TableHead>Aujourd’hui</TableHead>
                {modifiable ? <TableHead className="text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lignes.map((ligne) => {
                const situation = SITUATIONS[situationCamion(ligne)];
                return (
                  <TableRow key={ligne.id} data-testid={`inbound-truck-${ligne.id}`}>
                    <TableCell className="font-semibold">
                      <Link
                        to={`/trucks/${ligne.truck_id}?tab=location`}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {nomLisible(ligne.tractor_plate, 'Camion sans immatriculation')}
                      </Link>
                    </TableCell>
                    <TableCell>{nomLisible(ligne.tank_plate, '—')}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {formatDate(ligne.effective_start_date)}
                      {!ligne.start_date ? <span className="ml-1 text-xs text-muted-foreground">(du contrat)</span> : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {ligne.effective_end_date ? formatDate(ligne.effective_end_date) : 'Sans terme'}
                      {ligne.effective_end_date && !ligne.end_date ? (
                        <span className="ml-1 text-xs text-muted-foreground">(du contrat)</span>
                      ) : null}
                    </TableCell>
                    {canSeeMoney ? (
                      <TableCell className="text-right tabular-nums">
                        {ligne.rate_amount === null || ligne.rate_amount === undefined ? (
                          <span className="text-muted-foreground">Tarif du contrat</span>
                        ) : (
                          formatMontant(ligne.rate_amount, contract.currency)
                        )}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <Badge variant={situation.variant}>{situation.texte}</Badge>
                    </TableCell>
                    {modifiable ? (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Modifier les dates du camion"
                            onClick={() => ouvrirModification(ligne)}
                          >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Retirer le camion du contrat"
                            onClick={() => setARetirer(ligne)}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialog?.ligne ? 'Modifier le camion du contrat' : 'Ajouter un camion au contrat'}</DialogTitle>
            <DialogDescription>
              Laissez les dates vides pour suivre celles du contrat. Un camion ne peut pas être dans deux contrats
              sur la même période.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="inbound-truck-select">Camion</Label>
              {dialog?.ligne ? (
                <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm font-medium">
                  {nomLisible(dialog.ligne.tractor_plate, 'Camion sans immatriculation')}
                </p>
              ) : (
                <SearchableSelect
                  id="inbound-truck-select"
                  placeholder="Choisir un camion"
                  searchPlaceholder="Rechercher une immatriculation, un chauffeur…"
                  emptyMessage="Aucun camion trouvé"
                  value={form.truck_id}
                  onValueChange={(valeur) => setField('truck_id', valeur)}
                  options={options}
                />
              )}
              <FieldError show={submitAttempted && !dialog?.ligne && !form.truck_id}>
                Choisissez le camion à ajouter.
              </FieldError>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="inbound-truck-debut">Début pour ce camion</Label>
                <Input
                  id="inbound-truck-debut"
                  type="date"
                  value={form.start_date}
                  min={contract.start_date || undefined}
                  onChange={(event) => setField('start_date', event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inbound-truck-fin">Fin pour ce camion</Label>
                <Input
                  id="inbound-truck-fin"
                  type="date"
                  value={form.end_date}
                  onChange={(event) => setField('end_date', event.target.value)}
                />
              </div>
            </div>
            {canSeeMoney ? (
              <div className="space-y-1.5">
                <Label htmlFor="inbound-truck-tarif">Tarif propre à ce camion (facultatif)</Label>
                <Input
                  id="inbound-truck-tarif"
                  type="number"
                  min="0"
                  value={form.rate_amount}
                  placeholder="Vide : le tarif du contrat s’applique"
                  className="text-right tabular-nums"
                  onChange={(event) => setField('rate_amount', event.target.value)}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              Annuler
            </Button>
            <Button onClick={enregistrer} disabled={busy} data-testid="save-inbound-truck-btn">
              {busy ? 'Enregistrement…' : dialog?.ligne ? 'Enregistrer' : 'Ajouter le camion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(aRetirer)} onOpenChange={(open) => !open && setARetirer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer ce camion du contrat ?</AlertDialogTitle>
            <AlertDialogDescription>
              {nomLisible(aRetirer?.tractor_plate, 'Ce camion')} ne fera plus partie du contrat. Si ce camion a déjà été
              réglé, le retrait est refusé : donnez-lui alors une date de fin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={retirer} disabled={busy}>
              Retirer le camion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InboundTrucksPanel;
